import type { Identifier } from "ra-core";

import type {
  AdvisorAvailabilityBlock,
  OnGuardiaNowEntry,
  ProjectGuardiaRosterRow,
  ProjectOfficeHours,
  Sale,
  SalesProject,
} from "../types";
import { timeToMinutes } from "../leadAssignment/availabilityBlocks";

/**
 * Almikia Epic EL2 (TASK-074): pure rules of the "Guardias y asignacion" screen. A guardia is
 * always built FROM the availability an advisor marked, never imposed. Nothing here counts,
 * ranks or compares advisors, and no presence or attendance data exists.
 */

/** Guardias exist Monday to Friday only (database check). */
export const GUARDIA_WEEKDAYS = [1, 2, 3, 4, 5] as const;

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (date: string): Date => {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDate = (value: Date): string => value.toISOString().slice(0, 10);

/** Adds calendar days to a "YYYY-MM-DD" date (no time zone involved). */
export const addDays = (date: string, days: number): string =>
  formatDate(new Date(parseDate(date).getTime() + days * DAY_MS));

/** The Monday of the week that contains "YYYY-MM-DD". */
export const mondayOf = (date: string): string => {
  const jsDay = parseDate(date).getUTCDay();
  return addDays(date, jsDay === 0 ? -6 : 1 - jsDay);
};

/** The five guardia dates (Monday to Friday) of the week starting at `monday`. */
export const weekDates = (monday: string): string[] =>
  GUARDIA_WEEKDAYS.map((weekday) => addDays(monday, weekday - 1));

/** ISO weekday (1 = Monday .. 7) of "YYYY-MM-DD". */
export const weekdayOf = (date: string): number => {
  const jsDay = parseDate(date).getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
};

export interface TimeWindow {
  /** "HH:MM". */
  start: string;
  end: string;
}

const toHHMM = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60,
  ).padStart(2, "0")}`;

/**
 * The availability one advisor marked for a weekday, as sorted windows where touching or
 * overlapping blocks are merged. These are the ONLY windows the guardia form offers.
 */
export const availabilityWindows = (
  blocks: readonly Pick<
    AdvisorAvailabilityBlock,
    "sales_id" | "weekday" | "start_time" | "end_time" | "ended_at"
  >[],
  salesId: Identifier,
  weekday: number,
): TimeWindow[] => {
  const ranges = blocks
    .filter(
      (block) =>
        !block.ended_at &&
        block.weekday === weekday &&
        String(block.sales_id) === String(salesId),
    )
    .map((block) => ({
      start: timeToMinutes(block.start_time),
      end: timeToMinutes(block.end_time),
    }))
    .filter((range) => Number.isFinite(range.start) && range.end > range.start)
    .sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) {
      merged[merged.length - 1] = {
        start: last.start,
        end: Math.max(last.end, range.end),
      };
    } else {
      merged.push(range);
    }
  }
  return merged.map((range) => ({
    start: toHHMM(range.start),
    end: toHHMM(range.end),
  }));
};

/** True when [start, end] sits entirely inside one of the (merged) windows. */
export const isCoveredByAvailability = (
  windows: readonly TimeWindow[],
  start: string,
  end: string,
): boolean => {
  const from = timeToMinutes(start);
  const to = timeToMinutes(end);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
    return false;
  }
  return windows.some(
    (window) =>
      timeToMinutes(window.start) <= from && to <= timeToMinutes(window.end),
  );
};

/** Roster rows grouped by date (every requested date present), earliest start first. */
export const groupRosterByDate = (
  roster: readonly ProjectGuardiaRosterRow[],
  dates: readonly string[],
): Record<string, ProjectGuardiaRosterRow[]> =>
  Object.fromEntries(
    dates.map((date) => [
      date,
      roster
        .filter((row) => row.guardia_date === date)
        .sort(
          (a, b) =>
            timeToMinutes(a.start_time) - timeToMinutes(b.start_time) ||
            a.first_name.localeCompare(b.first_name),
        ),
    ]),
  );

/** Today's calendar date ("YYYY-MM-DD") in America/Mexico_City. */
export const mexicoToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

export type ProjectAdvisor = { id: Identifier; name: string };

/**
 * The active, enabled members of a project, sorted by name (a stable order, never by any
 * measure of performance).
 */
export const projectAdvisors = (
  projectId: Identifier,
  memberships: readonly Pick<
    SalesProject,
    "sales_id" | "project_id" | "ended_at"
  >[],
  sales: readonly Pick<Sale, "id" | "first_name" | "last_name" | "disabled">[],
): ProjectAdvisor[] => {
  const memberIds = new Set(
    memberships
      .filter(
        (row) => String(row.project_id) === String(projectId) && !row.ended_at,
      )
      .map((row) => String(row.sales_id)),
  );
  return sales
    .filter((sale) => memberIds.has(String(sale.id)) && !sale.disabled)
    .map((sale) => ({
      id: sale.id,
      name: `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim(),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/** The names shown under "En la sala de ventas ahora" (names only, stable order). */
export const onGuardiaNames = (entries: readonly OnGuardiaNowEntry[]) =>
  entries.map((entry) => `${entry.first_name} ${entry.last_name}`.trim());

export type LimitParse = { ok: true; value: number | null } | { ok: false };

/**
 * The cap of open unattended leads and the 'vencido' threshold are positive whole numbers; an
 * empty value means "Sin limite" (rule off, stored as null).
 */
export const parseLimit = (text: string): LimitParse => {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, value: null };
  if (!/^\d+$/.test(trimmed)) return { ok: false };
  const value = Number(trimmed);
  return value > 0 && Number.isSafeInteger(value)
    ? { ok: true, value }
    : { ok: false };
};

/** The text shown in a limit input for a stored value. */
export const limitToText = (value: number | null | undefined): string =>
  value == null ? "" : String(value);

/** Mexico has no DST since 2022: America/Mexico_City is a fixed UTC-6. */
const MEXICO_CITY_OFFSET = "-06:00";

export type UnavailabilityError = "required" | "end_not_after_start";

/**
 * Converts a Mexico City date + time ("YYYY-MM-DD", "HH:MM") to an ISO instant; null when
 * either part is empty or malformed.
 */
export const mexicoDateTimeToIso = (
  date: string,
  time: string,
): string | null => {
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !Number.isFinite(timeToMinutes(time))
  ) {
    return null;
  }
  const value = new Date(
    `${date}T${time.slice(0, 5)}:00.000${MEXICO_CITY_OFFSET}`,
  );
  return Number.isNaN(value.getTime()) ? null : value.toISOString();
};

/** Validates a 'No disponible' range (start and end, each date + time). */
export const buildUnavailabilityRange = (input: {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}):
  | { ok: true; starts_at: string; ends_at: string }
  | { ok: false; error: UnavailabilityError } => {
  const starts = mexicoDateTimeToIso(input.startDate, input.startTime);
  const ends = mexicoDateTimeToIso(input.endDate, input.endTime);
  if (!starts || !ends) return { ok: false, error: "required" };
  if (Date.parse(ends) <= Date.parse(starts)) {
    return { ok: false, error: "end_not_after_start" };
  }
  return { ok: true, starts_at: starts, ends_at: ends };
};

export type OfficeHoursDraft = { opens: string; closes: string };

export type OfficeHoursOperation =
  | { kind: "create"; weekday: number; opens_at: string; closes_at: string }
  | {
      kind: "update";
      id: Identifier;
      weekday: number;
      opens_at: string;
      closes_at: string;
    }
  | { kind: "delete"; id: Identifier; weekday: number };

export type OfficeHoursPlan =
  | { ok: true; operations: OfficeHoursOperation[] }
  | { ok: false; weekday: number; error: "required" | "end_not_after_start" };

/**
 * Turns the per-weekday inputs into the writes needed: a day with both times is created or
 * updated (only when it changed), a day with both times empty is closed (its row is deleted),
 * a half-filled day or a closing time that is not after the opening one is refused.
 */
export const planOfficeHoursSave = (
  existing: readonly Pick<
    ProjectOfficeHours,
    "id" | "weekday" | "opens_at" | "closes_at"
  >[],
  drafts: Readonly<Record<number, OfficeHoursDraft>>,
): OfficeHoursPlan => {
  const operations: OfficeHoursOperation[] = [];
  for (const weekday of [1, 2, 3, 4, 5, 6, 7]) {
    const draft = drafts[weekday] ?? { opens: "", closes: "" };
    const row = existing.find((hours) => hours.weekday === weekday);
    if (!draft.opens && !draft.closes) {
      if (row) operations.push({ kind: "delete", id: row.id, weekday });
      continue;
    }
    const opens = timeToMinutes(draft.opens);
    const closes = timeToMinutes(draft.closes);
    if (!Number.isFinite(opens) || !Number.isFinite(closes)) {
      return { ok: false, weekday, error: "required" };
    }
    if (closes <= opens) {
      return { ok: false, weekday, error: "end_not_after_start" };
    }
    const opens_at = `${draft.opens.slice(0, 5)}:00`;
    const closes_at = `${draft.closes.slice(0, 5)}:00`;
    if (!row) {
      operations.push({ kind: "create", weekday, opens_at, closes_at });
    } else if (row.opens_at !== opens_at || row.closes_at !== closes_at) {
      operations.push({
        kind: "update",
        id: row.id,
        weekday,
        opens_at,
        closes_at,
      });
    }
  }
  return { ok: true, operations };
};
